import {
  Box,
  Button,
  Card,
  Stack,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import * as Yup from 'yup';

import DepartmentDropdown from './departmentDropdown';
import EmailAddressTextField from './emailAddressTextField';
import FullNameTextField from './fullNameTextField';
import UserRoleDropdown from './userRoleDropdown';

import { blueGradientButton } from '../../../../assets/styles/blueGradientButton.style';
import inviteExternalUserIcon from '../../../../assets/svg/inviteExternalUser.svg';
import { axiosInstanceForTenantAPI } from '../../../../assets/utils/axiosInstance';
import { ROLE_VALUES } from '../../../../constants/customerConstant';
import { useAuth } from '../../../../contexts/auth/authContext';
import { useToastNotificationContext } from '../../../../hooks/global/alert/useToastNotification';
import DepartmentObject from '../../../../interfaces/departmentObject';
import { CustomError } from '../../../../interfaces/ErrorType';
import HeaderTextComponent from '../../../common/headerTextComponent';

interface Role {
  type: string;
  value: number;
}

interface InviteUserProps {
  departments: DepartmentObject[];
  roles: Role[];
  fetchLoading: boolean;
  setFetchLoading: React.Dispatch<React.SetStateAction<boolean>>;
  inviteLoading: boolean;
  setInviteLoading: React.Dispatch<React.SetStateAction<boolean>>;
  refetch: () => void;
}

const InviteUser = ({
  departments,
  fetchLoading,
  setFetchLoading,
  inviteLoading,
  setInviteLoading,
  roles,
  refetch,
}: InviteUserProps) => {
  const { slug } = useAuth();
  const intl = useIntl();
  const [companySlug, setCompanySlug] = useState<string | null>(slug);
  const [inviteExternalUser, setInviteExternalUser] = useState(false);
  const [role, setRole] = useState<Role[]>([]);

  useEffect(() => {
    if (!slug) {
      setCompanySlug(localStorage.getItem('slug'));
    }
  }, [slug]);

  const { createToastNotification } = useToastNotificationContext();

  useEffect(() => {
    const rolesCopy = [...roles];
    if (!inviteExternalUser) {
      setRole(rolesCopy.slice(0, 2));
    } else {
      setRole(rolesCopy.splice(2));
    }
  }, [inviteExternalUser, roles]);

  const formik = useFormik({
    initialValues: {
      fullName: '',
      email: '',
      userRole: '',
      department: '',
      submit: null,
    },
    validationSchema: Yup.object({
      fullName: Yup.string().required(
        intl.formatMessage({ defaultMessage: 'Full Name Required.' }),
      ),
      email: Yup.string()
        .max(255)
        .required(intl.formatMessage({ defaultMessage: 'Email Required.' }))
        .email(intl.formatMessage({ defaultMessage: 'Enter valid email.' })),
      userRole: Yup.string()
        .max(255)
        .required(
          intl.formatMessage({ defaultMessage: 'User Role Required.' }),
        ),
      department: !inviteExternalUser
        ? Yup.string()
          .max(255)
          .required(
            intl.formatMessage({ defaultMessage: 'Department Required.' }),
          )
        : Yup.string(),
    }),
    onSubmit: async (values, helpers) => {
      setFetchLoading(true);
      setInviteLoading(true);
      try {
        const inviteUserResponse = await axiosInstanceForTenantAPI(
          companySlug,
        ).post('/invite-user', {
          email: values.email,
          user_type: values.userRole,
          department: values.department,
          full_name: values.fullName,
        });

        const [inviteUser] = await Promise.allSettled([inviteUserResponse]);
        if (inviteUser.status === 'fulfilled') {
          if (!inviteExternalUser)
            createToastNotification(
              intl.formatMessage({
                defaultMessage: 'User invitation successfully sent.',
              }),
              'success',
            );
          else
            createToastNotification(
              intl.formatMessage({
                defaultMessage:
                  'External User Added: Note, access for external users is limited. Please adjust their access settings to grant required permissions.',
              }),
              'success',
            );
          formik.setValues({
            fullName: '',
            email: '',
            userRole: '',
            department: '',
            submit: null,
          });
          formik.setTouched({});
          formik.setErrors({});
          refetch();
        } else {
          throw new Error(
            intl.formatMessage(
              {
                defaultMessage: 'Error submitting the report: {reason}',
              },
              {
                reason: inviteUser.reason,
              },
            ),
          );
        }
      } catch (error) {
        helpers.setStatus({ success: false });
        const customError = error as CustomError;
        const errorMessage =
          customError.response?.data?.email ||
          customError.response?.data?.message ||
          customError.response?.data?.detail ||
          (Array.isArray(customError.response?.data) &&
            customError?.response?.data[0]) ||
          intl.formatMessage({ defaultMessage: 'Something went wrong' });
        helpers.setErrors({ submit: errorMessage });
        helpers.setSubmitting(false);
      } finally {
        setFetchLoading(false);
        setInviteLoading(false);
        setInviteExternalUser(false);
      }
    },
  });

  const handleInviteExternalUser = () => {
    formik.values.userRole = '';
    setInviteExternalUser(!inviteExternalUser);
  };

  const roleValueChange = () => {
    if (inviteExternalUser) {
      formik.values.department = '';
      formik.values.userRole = ROLE_VALUES.EXTERNAL_USER.toString();
    }
    return formik.values.userRole;
  };

  return (
    <Box sx={{ width: '100%' }}>
      <form noValidate onSubmit={formik.handleSubmit}>
        <Card
          sx={{
            borderRadius: '6px',
            border: '1px solid #00000029',
            padding: '24px 20px 2px 20px',
            boxShadow: '0px 0px 4px 0px #0000000A',
            width: '100%',
            opacity: fetchLoading ? '0.7' : '1',
            pointerEvents: fetchLoading ? 'none' : 'auto',
          }}
        >
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            sx={{ mb: 2 }}
          >
            <HeaderTextComponent variant='h1'>
              {!inviteExternalUser
                ? intl.formatMessage({ defaultMessage: 'Invite User' })
                : intl.formatMessage({ defaultMessage: 'External User' })}
            </HeaderTextComponent>
            <Stack direction='row' alignItems='center' spacing={2}>
              <Button
                startIcon={
                  !inviteExternalUser && (
                    <img
                      src={inviteExternalUserIcon}
                      alt='invite-external-user-icon'
                    />
                  )
                }
                disabled={inviteLoading}
                sx={{
                  textTransform: 'none',
                  fontSize: 14,
                  color: !inviteExternalUser ? '#6D6D6D' : '#181818',
                  fontWeight: !inviteExternalUser ? 500 : 600,
                  '&:hover': {
                    color: 'inherit',
                    background: 'inherit',
                  },
                }}
                onClick={handleInviteExternalUser}
              >
                {!inviteExternalUser
                  ? intl.formatMessage({
                    defaultMessage: 'Invite External User',
                  })
                  : intl.formatMessage({ defaultMessage: 'Cancel' })}
              </Button>
              <Button
                type='submit'
                disabled={inviteLoading}
                sx={{
                  fontStyle: 'normal',
                  fontWeight: 600,
                  ...blueGradientButton,
                  textTransform: 'none',
                }}
              >
                {inviteLoading ? (
                  <>
                    <CircularProgress
                      size={20}
                      style={{ marginRight: '8px', color: 'black' }}
                    />
                  </>
                ) : (
                  intl.formatMessage({ defaultMessage: 'Invite' })
                )}
              </Button>
            </Stack>
          </Stack>
          <Stack
            direction='row'
            alignItems='flex-start'
            justifyContent='space-between'
            sx={{ width: '100%', paddingBottom: '.5rem' }}
            spacing={2}
          >
            <FullNameTextField
              error={!!(formik.touched.fullName && formik.errors.fullName)}
              helperText={
                formik.touched.fullName && formik.errors.fullName
                  ? formik.errors.fullName
                  : ''
              }
              onChange={formik.handleChange}
              userFullName={formik.values.fullName}
              onBlur={formik.handleBlur}
            />

            <EmailAddressTextField
              error={!!(formik.touched.email && formik.errors.email)}
              helperText={
                formik.touched.email && formik.errors.email
                  ? formik.errors.email
                  : ''
              }
              onChange={formik.handleChange}
              emailValue={formik.values.email}
              onBlur={formik.handleBlur}
            />
            <UserRoleDropdown
              error={!!(formik.touched.userRole && formik.errors.userRole)}
              helperText={
                formik.touched.userRole && formik.errors.userRole
                  ? formik.errors.userRole
                  : ''
              }
              onChange={formik.handleChange}
              userRoleValue={roleValueChange()}
              onBlur={formik.handleBlur}
              roles={role}
            />
            {!inviteExternalUser && (
              <DepartmentDropdown
                error={
                  !!(formik.touched.department && formik.errors.department)
                }
                helperText={
                  formik.touched.department && formik.errors.department
                    ? formik.errors.department
                    : ''
                }
                onChange={formik.handleChange}
                departmentValue={formik.values.department}
                onBlur={formik.handleBlur}
                departments={departments}
              />
            )}
          </Stack>
          <Stack
            direction='row'
            alignItems='flex-start'
            justifyContent='space-between'
            sx={{ width: '100%' }}
            spacing={2}
          >
            {formik.errors.submit && (
              <Typography
                color='error'
                variant='body2'
                sx={{ padding: '10px 0 10px 0' }}
              >
                {formik.errors.submit}
              </Typography>
            )}
          </Stack>
        </Card>
      </form>
    </Box>
  );
};

export default InviteUser;
